import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useSelector } from 'react-redux';

import {
  QuestionDetails,
  QuestionPoints,
  QuestionText,
  useSelectQuestion,
} from '~teacher/components/QuizQuestion';
import {
  useHandleChangeWeight,
  useHandleImageChange,
  useHandleQuestionTextChange,
} from '~teacher/components/QuizQuestion/hooks';
import { QuestionMedia } from '~teacher/components/QuizQuestion/QuestionMedia';
import { proCheck } from '~teacher/util/proCheck';

import { makeSelectIsControlsDisabled } from '../../slices/quiz/quizSelectors';
import QuestionTrueFalseDisplayAnswer from './QuestionTrueFalseDisplayAnswer';
import QuestionTrueFalseEditAnswer from './QuestionTrueFalseEditAnswer';

interface Props {
  questionId: string;
  order: number;
  hasCustomGradingWeight?: boolean;
  isLocked: boolean;
}
export default function QuestionTrueFalse({
  questionId,
  order,
  isLocked,
  hasCustomGradingWeight = false,
}: Props) {
  const isDisabled = useSelector(makeSelectIsControlsDisabled());
  const {
    answers,
    explanation,
    explanationImage,
    gradingWeight,
    questionText,
    type,
    isEditing,
  } = useSelectQuestion({ questionId: questionId });

  const showAdvancedQuizEditor = proCheck.canShowAdvancedQuizEditor();

  const handleQuestionTextChange = useHandleQuestionTextChange();
  const handleChangeWeight = useHandleChangeWeight();
  const handleQuestionImageChange = useHandleImageChange();

  return (
    <div className="question-grid flex-1" data-question={type}>
      <QuestionText
        isLocked={isLocked}
        isDisabled={isDisabled}
        questionId={questionId}
        order={order}
        text={questionText}
        weight={gradingWeight}
        placeholder={translate('Have a true-or-false question to ask?')}
        isEditing={isEditing}
        onQuestionTextChange={handleQuestionTextChange}
        onChangeWeight={handleChangeWeight}
        showAdvancedQuizEditor={showAdvancedQuizEditor}
      />
      <div
        className={cx(
          'flex xl:justify-end w-124 h-fit mt-6 -ml-6 sm:ml-15 xl:mt-0 xl:ml-0 lg:row-start-2 xl:row-span-2 xl:col-start-2 xl:text-right',
          {
            'justify-center sm:justify-start': isEditing,
          }
        )}
      >
        <QuestionMedia
          questionId={questionId}
          onChangeImage={handleQuestionImageChange}
        />
      </div>
      <div
        className={`xl:row-start-2 lg:row-start-3 ${
          isEditing ? 'mt-4 -mr-12 md:mr-0' : ''
        }`}
      >
        <QuestionPoints
          isHidden={!hasCustomGradingWeight || isEditing}
          weight={gradingWeight}
        />
        <div className="grid grid-cols-question-fields-question-editor p-0 mt-4">
          <div className="col-span-2 col-start-3 flex items-start pl-2">
            {isEditing ? (
              <QuestionTrueFalseEditAnswer
                answerIds={answers}
                isDisabled={isDisabled}
              />
            ) : (
              <QuestionTrueFalseDisplayAnswer answerIds={answers} />
            )}
          </div>
        </div>
        <QuestionDetails
          questionId={questionId}
          content={explanation}
          isDisabled={isDisabled}
          image={explanationImage}
          isEditing={isEditing}
        />
      </div>
    </div>
  );
}
